import { useMemo } from 'react'
import styles from './ProductPageTemplate.module.css'
import Breadcrumbs, { CATEGORY_TYPES } from 'components/product/Breadcrumbs'
import DealCtaPill from 'components/product/DealCtaPill'
import OnThisPageNav, { type TocSection } from 'components/product/OnThisPageNav'
import ProductHero from 'components/product/ProductHero'
import KeySpecs from 'components/product/KeySpecs'
import VerdictBlock from 'components/product/VerdictBlock'
import LabData from 'components/product/LabData'
import RelatedComparisons from 'components/product/RelatedComparisons'
import Videos from 'components/product/Videos'
import FeaturesOpinions from 'components/product/FeaturesOpinions'
import RelatedProducts from 'components/product/RelatedProducts'
import OwnersSection, { OWNERS_SECTION_ID } from 'components/owner-notes/OwnersSection'
import ReaderQuestions, {
  READER_QUESTIONS_ID,
  type ReaderQuestionsData,
} from 'components/product/ReaderQuestions'
import { type ProductCardModel } from 'components/product/cardModel'
import type { SoundGuysReviewModel } from 'lib/dealhunt/review'
import {
  amazonUrl,
  brandedBreadcrumbs,
  brandedTitle,
  dealHuntCategoryHref,
  dealHuntProductHref,
  productCategoryLeaf,
  productCategoryPath,
  productDisplayName,
} from 'lib/dealhunt/url'
import { RAIL_SORT } from 'lib/dealhunt/constants'
import { publicConfig } from 'config'
import {
  CatalogAnalyticsProvider,
  useSectionViews,
  type CatalogAnalyticsScope,
} from 'lib/analytics/catalog'
import type { LabChartPaths } from 'lib/dealhunt/audioChart'
import type {
  DealHuntProductLine,
  RatingDistributionsResponse,
  SpecDistributionsResponse,
} from 'lib/dealhunt/types'

interface ProductPageTemplateProps {
  data: DealHuntProductLine
  alternatives: ProductCardModel[]
  specDistributions: SpecDistributionsResponse | null
  /** Class score distributions for the featured-scores card; null → rings only. */
  ratingDistributions: RatingDistributionsResponse | null
  /** Real lab curves (measurement app, via `loadLabCharts`), or `null` — the Lab data
   *  section is hidden when null. */
  labCharts: LabChartPaths | null
  /** `buildSoundGuysReview(data.enrichment)`, built once by the page (its title needs it
   *  too); null when the line has no SoundGuys review. */
  review: SoundGuysReviewModel | null
  /** "Questions from readers" (community discussions): DealHunt's cards for this product, or null
   *  — none, a DealHunt that could not say, or one that ignored the product filter. Null leaves
   *  the module AND its nav entry out. */
  readerQuestions?: ReaderQuestionsData | null
}

const ETHICS =
  'All products featured are independently chosen by us. However, SoundGuys may ' +
  'receive a commission on orders placed through its retail links. See our ethics statement.'

/** Fires `product_page_section_view` per section — must live INSIDE the analytics provider. */
const SectionViews: React.VFC<{ ids: string[] }> = ({ ids }) => {
  useSectionViews(ids)
  return null
}

const ProductPageBody: React.VFC<ProductPageTemplateProps> = ({
  data,
  alternatives,
  specDistributions,
  ratingDistributions,
  labCharts,
  review,
  readerQuestions = null,
}) => {
  const { specs, videos } = data.enrichment
  const versus = data.enrichment.versus ?? []
  const opinions = data.enrichment.opinions ?? []

  const hasSpecs = specs.length > 0
  const hasReview = review !== null
  const hasLab = labCharts !== null
  // What the bridge in the hero promises — the tabs LabData will actually render.
  const labSummary = labCharts
    ? [
        labCharts.frequency ? 'Frequency response' : null,
        labCharts.isolation ? 'Isolation' : null,
        labCharts.anc ? 'Noise cancelling' : null,
        review?.mdaqs.length ? 'Audio quality' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null
  const hasVideos = videos.length > 0
  const hasVersus = versus.length > 0
  const hasOpinions = opinions.length > 0
  const hasAlternatives = alternatives.length > 0

  const amazonHref = data.primaryProduct
    ? amazonUrl(
        data.primaryProduct.asin,
        data.primaryProduct.domain,
        data.primaryProduct.shortIoUrl
      )
    : null

  // The hero H1's name (e.g. "Nothing Headphone (1)" rather than the bare leaf
  // "Headphone"), so Lab data, Owners, Compare and Videos name the product the same way.
  const productTitle = productDisplayName(data)

  // Final (product) crumb brand-prefixed — the same trail the JSON-LD carries.
  const breadcrumbs = brandedBreadcrumbs(data)

  // "See our top picks" (rail header) → the product's own category page — the deepest
  // category breadcrumb (the related-node rail scope carries no fullSlug) — pre-sorted
  // by the rail's own sort so the listing continues the rail's ordering. RAIL_SORT is a
  // legal category-page sortBy; were it ever repinned to a non-dropdown key, parseFilters
  // coerces the unknown value to the default sort rather than breaking the page.
  const categoryCrumb = [...data.breadcrumbs]
    .reverse()
    .find(crumb => CATEGORY_TYPES.has(crumb.nodeType))
  const topPicksHref = categoryCrumb
    ? `${dealHuntCategoryHref(
        categoryCrumb.fullSlug,
        publicConfig('useTrailingSlash')
      )}?sortBy=${RAIL_SORT}`
    : undefined

  // Every section is data-gated: Lab data renders only when real lab curves resolved.
  const sections: TocSection[] = [
    { id: 'sec-overview', label: 'Overview' },
    hasSpecs ? { id: 'sec-specs', label: 'Specs' } : null,
    // Lab data sits above the review on tested products: measurements, then verdict.
    hasLab ? { id: 'sec-labs', label: 'Lab data' } : null,
    hasReview ? { id: 'sec-review', label: 'Review' } : null,
    // Owners is ALWAYS present (contract #37/#51): the section renders from the first
    // published note and carries the ask below that — never behind a tab, never gated.
    { id: OWNERS_SECTION_ID, label: 'Owners' },
    // The module always renders (its ask); the nav entry waits for a card to jump to.
    readerQuestions?.items.length ? { id: READER_QUESTIONS_ID, label: 'Questions' } : null,
    hasVersus ? { id: 'sec-compare', label: 'Compare' } : null,
    hasVideos ? { id: 'sec-videos', label: 'Videos' } : null,
    hasOpinions ? { id: 'sec-opinions', label: 'Opinions' } : null,
    hasAlternatives ? { id: 'sec-more', label: 'More' } : null,
  ].filter((s): s is TocSection => s !== null)

  return (
    <>
      <OnThisPageNav sections={sections} amazonHref={amazonHref} />
      <SectionViews ids={sections.map(s => s.id)} />

      <div className={styles.page}>
        <div className={styles.overview}>
          <div id="sec-overview" className={styles.anchor}>
            <div className={styles.breadcrumbs}>
              <Breadcrumbs items={breadcrumbs} />
              <DealCtaPill className={styles.dealCta} />
            </div>
            <p className={styles.ethics}>{ETHICS}</p>
          </div>
          <ProductHero
            data={data}
            specDistributions={specDistributions}
            review={review}
            ratingDistributions={ratingDistributions}
            labHref={hasLab ? '#sec-labs' : null}
            labSummary={labSummary}
          />
        </div>

        {hasSpecs && (
          <div id="sec-specs" className={styles.anchor}>
            <KeySpecs specs={specs} specDistributions={specDistributions} />
          </div>
        )}

        {hasLab && labCharts && (
          <div id="sec-labs" className={styles.anchor}>
            <LabData
              curves={labCharts}
              mdaqs={review?.mdaqs ?? []}
              productName={productTitle}
              comparisons={data.enrichment.labComparisons ?? []}
              nodeId={data.node.id}
            />
          </div>
        )}

        {hasReview && review && (
          <div id="sec-review" className={styles.anchor}>
            <VerdictBlock review={review} />
          </div>
        )}

        <div id={OWNERS_SECTION_ID} className={styles.anchor}>
          <OwnersSection
            // Pages-router client navigation reuses the instance: the key resets the
            // notes/count/page seeded from props, so product B never shows A's owners.
            key={data.node.id}
            nodeId={data.node.id}
            productName={productTitle}
            owners={data.enrichment.owners}
            productHref={dealHuntProductHref(
              data.node.fullSlug,
              data.brandNode?.name ?? null,
              publicConfig('useTrailingSlash'),
              data.brandNode?.slug ?? null
            )}
          />
        </div>

        {readerQuestions && (
          <div id={READER_QUESTIONS_ID} className={styles.anchor}>
            <ReaderQuestions {...readerQuestions} productName={productTitle} />
          </div>
        )}

        {hasVersus && (
          <div id="sec-compare" className={styles.anchor}>
            <RelatedComparisons
              productName={productTitle}
              productImageUrl={data.heroImage ?? data.node.imageUrl}
              versus={versus}
            />
          </div>
        )}

        {hasVideos && (
          <div id="sec-videos" className={styles.anchor}>
            <Videos videos={videos} productName={productTitle} />
          </div>
        )}

        {hasOpinions && (
          <div id="sec-opinions" className={styles.anchor}>
            <FeaturesOpinions opinions={opinions} />
          </div>
        )}

        {hasAlternatives && (
          <div id="sec-more" className={styles.anchor}>
            <RelatedProducts
              products={alternatives}
              topPicksHref={topPicksHref}
              title={(() => {
                // Match the heading to the rail's scope: the curated subcategory when
                // present (the rail is scoped to it), else the broad related-node name.
                const name = data.node.relatedSubcategory?.name ?? data.node.relatedNodeName
                return name ? `More ${name}` : 'Related products'
              })()}
            />
          </div>
        )}
      </div>
    </>
  )
}

const ProductPageTemplate: React.VFC<ProductPageTemplateProps> = props => {
  const { data, labCharts, review } = props
  const tested = review !== null
  // Common params for every product_page_* event (docs/analytics-catalog-events.md).
  // Memoised: the provider publishes this object as the page scope on identity change.
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'product_page',
      product_id: data.node.id,
      product_name: brandedTitle(data.brandNode?.name, data.node.name),
      product_slug: data.node.fullSlug,
      brand: data.brandNode?.name ?? '',
      // `category` = the leaf node name everywhere (category page, page views); the
      // full trail is its own dimension.
      category: productCategoryLeaf(data.node),
      category_path: productCategoryPath(data.node),
      tested,
      has_lab_data: labCharts !== null,
    }),
    [data.node, data.brandNode?.name, tested, labCharts]
  )
  return (
    <CatalogAnalyticsProvider value={scope}>
      <ProductPageBody {...props} />
    </CatalogAnalyticsProvider>
  )
}

export default ProductPageTemplate
